<template>
    <div class="target-with-variants">

        <el-popover :visible="focused && variantsPopupVisible"
                    placement="bottom"
                    :show-arrow="false"
                    :width="1160"
                    popper-class="variants-popover">

            <div class="header-row">
                <div class="popover-name">
                    <div>
                        Translation variants
                    </div>
                </div>

                <div class="time-stamp-wrapper">
                    <div class="time-stamp">
                        {{variantsTimestamp}}
                    </div>

                    <div class="close-icon">
                        <span @click="closeVariantsPopup"
                              class="material-symbols-outlined">
                            close
                        </span>
                    </div>
                </div>

            </div>

            <TranslationVariants v-if="variantsPopupVisible && focused"
                                 v-model="segmentItem"
                                 :configuration="configuration"
                                 :loading="segmentLoading" />

            <template #reference>
                <span></span>
            </template>

        </el-popover>

    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { useI18n } from 'vue-i18n'

    import { segmentsStore } from '@/stores/segmentsStore'

    import { SegmentEditorItem } from '@/models/SegmentEditorItem';

    import TranslationVariants from './Variants/TranslationVariants.vue';


    export default defineComponent({
        props: {
            modelValue: {
                type: Object as PropType<SegmentEditorItem>,
                required: true
            }
        },
        components: {
            TranslationVariants,
        },
        data() {
            return {
                segmentsStore: segmentsStore(),
                variantsTimestamp: '',
                openPopupTimeout: null as ReturnType<typeof setTimeout> | null,
            }
        },
        setup() {
            const { t } = useI18n()
            return { t }
        },
        computed: {
            segmentItem(): SegmentEditorItem {
                return this.modelValue;
            },
            variantsPopupVisible(): boolean {
                return this.segmentItem.variantsPopup;
            },
            focused(): boolean {
                return this.segmentItem.focused;
            },
            segmentLoading(): boolean {
                return this.segmentItem.loading;
            },
            configuration(): any {
                return this.segmentsStore.translationConfiguration;
            },
        },
        watch: {
            focused(newVal: boolean) {
                if (newVal) {
                    this.openTranslationVariants();
                } else {
                    this.closeVariantsPopup()
                }
            },
        },
        mounted() {
            if (this.focused) {
                this.openTranslationVariants();
            }
        },
        beforeUnmount() {
            this.clearTimeout();
        },

        methods: {
            clearTimeout() {
                if (this.openPopupTimeout) {
                    clearTimeout(this.openPopupTimeout);
                    this.openPopupTimeout = null;
                }
            },
            openTranslationVariants() {
                this.clearTimeout();
                
                if (this.variantsPopupNeeded()) {
                    this.openPopupTimeout = setTimeout(() => {
                        // Only open if component is still focused
                        if (this.focused) {
                            this.openVariantsPopup();
                        }
                        this.openPopupTimeout = null;
                    }, 350);
                }
            },
            variantsPopupNeeded(): boolean {
                return this.segmentsStore.translationConfiguration?.openVariantsPopup ?? false;
            },
            openVariantsPopup() {
                this.segmentItem.variantsPopup = true;
                this.variantsTimestamp = this.t('translationVariants.loaddedAgo', [this.formatTimestamp()]);
            },
            closeVariantsPopup() {
                this.clearTimeout();
                this.segmentItem.variantsPopup = false;
            },
            formatTimestamp(): string {
                const seconds = this.variantsLoaddedSecondsAgo();

                const timeUnits = [
                    { divisor: 31536000, key: 'years' },
                    { divisor: 2592000, key: 'months' },
                    { divisor: 86400, key: 'days' },
                    { divisor: 3600, key: 'hours' },
                    { divisor: 60, key: 'min' },
                ];

                for (const { divisor, key } of timeUnits) {
                    const interval = seconds / divisor;
                    if (interval >= 1) {
                        return `${Math.floor(interval)} ${this.t(`translationVariants.${key}`)}`;
                    }
                }

                return `${Math.floor(seconds)} ${this.t('translationVariants.sec')}`;
            },
            variantsLoaddedSecondsAgo(): number {
                const translationDate = this.segmentItem.data?.translationVariants?.translationDate;
                
                if (!translationDate) {
                    return 0;
                }
                
                const date = new Date(translationDate);
                if (isNaN(date.getTime())) {
                    return 0;
                }
                
                const timeDiff = Date.now() - date.getTime();
                return Math.max(0, Math.floor(timeDiff / 1000));
            },
        }

    })

</script>

<style lang="scss" scoped>
    .header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .popover-name {
            display: flex;
            align-items: center;
            color: var(--text-color);
            font-size: 17px;
            font-weight: 600;
        }

        .time-stamp-wrapper {
            display: flex;
        }

        .time-stamp {
            margin-right: 40px;
            color: gray;
        }

        .close-icon {
            cursor: pointer;
            opacity: 0.6;
            transition: 0.3s;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            color: red;
            margin-bottom: 12px;
        }
    }

    .target-with-variants {
        display: flex;
        flex-direction: row;

    }
</style>
